import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import BingoCard from "./BingoCard";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import QRCode from "qrcode.react";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const CardFetcher = ({ selectedCards }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useSelector((state) => state.user);
  const [showQRModal, setShowQRModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [file, setFile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const qrRef = useRef();
  const [cardColor, setCardColor] = useState('default');
  const [page, setPage] = useState(1);
  const [hasMoreCards, setHasMoreCards] = useState(true);
  const [branchPage, setBranchPage] = useState(1);
  const [hasMoreBranches, setHasMoreBranches] = useState(true);
  const [branchLoading, setBranchLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editFormData, setEditFormData] = useState(null);

  const fetchBranches = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      setBranchLoading(true);
      setError(null);
      
      let response;
      const limit = 50;
      const params = new URLSearchParams({ page, limit }).toString();
      
      if (currentUser?.role === "superadmin") {
        response = await fetch(`/api/branch/branch?${params}`);
      } else {
        response = await fetch(`/api/branch/getbranch/${currentUser?.username}?${params}`);
      }
      
      if (!response.ok) throw new Error("Failed to fetch branches");
      
      const data = await response.json();
      const branchesData = data?.branches || data || [];
      
      if (isLoadMore) {
        setBranches(prev => [...prev, ...branchesData]);
      } else {
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      }
      
      setHasMoreBranches(branchesData.length >= limit);
      setBranchPage(page);
    } catch (err) {
      console.error("Branch fetch error:", err);
      setError("Error fetching branches");
      setBranches([]);
    } finally {
      setBranchLoading(false);
    }
  }, [currentUser]);

  const loadMoreBranches = () => {
    fetchBranches(branchPage + 1, true);
  };

  useEffect(() => {
    fetchBranches(1);
  }, [fetchBranches]);

  const fetchCards = useCallback(async (branch, page = 1, isLoadMore = false) => {
  if (!branch) return;
  
  const abortController = new AbortController();
  const limit = 100;
  
  try {
    setLoading(true);
    setError(null);
    
    const response = await axios.get(`/api/card/getCards`, {
      params: { branch, page, limit },
      signal: abortController.signal
    });
    
    const responseData = response.data;
    const cardsData = responseData?.cards || [];
    
    // Sort cards by ID in ascending order (min to max)
    const sortedCards = cardsData.sort((a, b) => {
      // Convert IDs to numbers if they're strings
      const idA = typeof a.id === 'string' ? parseInt(a.id, 10) : a.id;
      const idB = typeof b.id === 'string' ? parseInt(b.id, 10) : b.id;
      return idA - idB;
    });
    
    if (!abortController.signal.aborted) {
      if (isLoadMore) {
        // When loading more, merge and sort all cards again
        const mergedCards = [...cards, ...sortedCards];
        const finalSorted = mergedCards.sort((a, b) => {
          const idA = typeof a.id === 'string' ? parseInt(a.id, 10) : a.id;
          const idB = typeof b.id === 'string' ? parseInt(b.id, 10) : b.id;
          return idA - idB;
        });
        setCards(finalSorted);
      } else {
        setCards(sortedCards);
      }
      setHasMoreCards(sortedCards.length > 0 && page < responseData.pages);
      setPage(page);
    }
  } catch (error) {
    if (!abortController.signal.aborted) {
      console.error("Card fetch error:", error);
      setError(error.response?.data?.message || "Failed to fetch cards");
      setCards([]);
    }
  } finally {
    if (!abortController.signal.aborted) {
      setLoading(false);
    }
  }

  return () => abortController.abort();
}, [cards]); // Add cards to dependencies to avoid stale closure

  const loadMoreCards = () => {
    fetchCards(branch, page + 1, true);
  };

  const handleBranchChange = (e) => {
    setBranch(e.target.value);
    setPage(1);
  };

  const handleSearch = useCallback(() => {
    if (branch) {
      fetchCards(branch, 1);
    } else {
      setError("Please select a branch");
    }
  }, [branch, fetchCards]);

  const handleDeleteCard = async (card) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete cartella ${card.id}?`);
    if (!confirmDelete) return;

    try {
      // Backend expects the cartella ID (not MongoDB _id)
      await axios.delete(`/api/card/deletecard/${card.id}`);
      alert("Card deleted successfully!");
      // Refresh the current page of cards
      fetchCards(branch, 1, false);
    } catch (err) {
      console.error("Delete error:", err);
      alert(err.response?.data?.message || "Error deleting card");
      setError(err.response?.data?.message || "Error deleting card");
    }
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setEditFormData({
      id: card.id,
      branch: card.branch,
      B: card.card.B || ["", "", "", "", ""],
      I: card.card.I || ["", "", "", "", ""],
      N: card.card.N || ["", "", "Free", "", ""],
      G: card.card.G || ["", "", "", "", ""],
      O: card.card.O || ["", "", "", "", ""],
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingCard(null);
    setEditFormData(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value,
    });
  };

  const handleEditArrayChange = (e, column, index) => {
    const { value } = e.target;

    if (value !== "" && (isNaN(value) || value < 1 || value > 99)) {
      return;
    }

    const updatedColumn = [...editFormData[column]];
    if (column === "N" && index === 2) {
      updatedColumn[index] = "Free";
    } else {
      updatedColumn[index] = value;
    }

    setEditFormData({
      ...editFormData,
      [column]: updatedColumn,
    });
  };

  const checkForRepeatedNumbers = (formData) => {
    const allNumbers = [
      ...formData.B,
      ...formData.I,
      ...formData.G,
      ...formData.O,
      ...formData.N.filter((val, idx) => idx !== 2),
    ].filter((num) => num !== "" && num !== "Free");

    const uniqueNumbers = new Set(allNumbers);
    return uniqueNumbers.size !== allNumbers.length;
  };

  const handleUpdateCard = async (e) => {
    e.preventDefault();

    if (checkForRepeatedNumbers(editFormData)) {
      alert("There are repeated numbers in the card. Please check and ensure all numbers are unique.");
      return;
    }

    const cardData = {
      id: editFormData.id,
      branch: editFormData.branch,
      card: {
        B: editFormData.B.map(num => num === "" ? 0 : Number(num)),
        I: editFormData.I.map(num => num === "" ? 0 : Number(num)),
        N: editFormData.N.map((val) => (val === "Free" ? val : val === "" ? 0 : Number(val))),
        G: editFormData.G.map(num => num === "" ? 0 : Number(num)),
        O: editFormData.O.map(num => num === "" ? 0 : Number(num)),
      },
    };

    try {
      setLoading(true);
      const response = await axios.put(`/api/card/update/${editingCard.id}`, cardData);
      alert(response.data?.message || "Card updated successfully!");
      handleCloseEditModal();
      fetchCards(branch, 1, false);
    } catch (error) {
      alert(error.response?.data?.message || "Error updating card");
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleGenerateQR = async () => {
    if (!file) {
      setError("Please upload a PDF file");
      return;
    }

    setLoading2(true);
    setError(null);

    try {
      const storageRef = ref(storage, `${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setPdfUrl(downloadURL);
      setShowQRModal(true);
    } catch (error) {
      console.error("QR generation error:", error);
      setError("Failed to generate QR code");
    } finally {
      setLoading2(false);
    }
  };

  const handleCloseQRModal = useCallback(() => {
    setShowQRModal(false);
    setFile(null);
  }, []);

  const downloadQRCode = useCallback(() => {
    if (!qrRef.current) return;
    
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "QRCode.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }, []);

  useEffect(() => {
    return () => {
      setCards([]);
      setBranches([]);
    };
  }, []);

  return (
    <div className="tw-bg-gray-100 tw-min-h-screen">
      <div className="tw-pt-7 tw-items-center tw-flex-col tw-justify-center tw-text-center branch-input">
        <Link
          to="/admin"
          className="tw-border-2 tw-p-1 tw-px-4 tw-border-blue-800 tw-text-blue-800"
        >
          Back
        </Link>
        <label htmlFor="branch" className="tw-text-lg tw-font-bold">
          Branch:{" "}
        </label>
        <div className="tw-relative">
          <select
            id="branch"
            onChange={handleBranchChange}
            value={branch}
            className="tw-dark:bg-slate-100 sm:tw-w-[390px] tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5"
          >
            <option value="">Select Branch</option>
            {branches.map((branch) => (
              <option key={branch._id || branch.id} value={branch.name}>
                {branch.name}
              </option>
            ))}
          </select>
          {hasMoreBranches && (
            <button
              onClick={loadMoreBranches}
              disabled={branchLoading}
              className="tw-absolute tw-right-1 tw-bottom-1 tw-px-2 tw-py-1 tw-bg-blue-600 tw-text-white tw-text-xs tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {branchLoading ? 'Loading...' : 'Load More'}
            </button>
          )}
        </div>
        <button 
          onClick={handleSearch}
          disabled={!branch}
          className="tw-mt-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          Search
        </button>
      </div>

      {loading && <p className="tw-text-center tw-py-4">Loading cards...</p>}
      {error && <p className="tw-text-red-500 tw-text-center tw-py-4">{error}</p>}

      {!loading && !error && cards.length > 0 && (
        <div>
          <div className="row justify-start align-center margin-vertical-md">
            <div className="col shrink padding-horizontal-md">
              <button 
                onClick={() => setCardColor(prev => prev === 'default' ? 'red' : 'default')}
                className="secondary-button"
              >
                switch design
              </button>
            </div>
          </div>
          <div className="row card-block justify-center margin-vertical-lg">
            <div className="col text-center" style={{ gap: '40px' }}>
              {cards.map((cardData, index) => (
                <div 
                  key={index} 
                  className="card blue tw-text-blue-800" 
                  style={{
                    ...(cardColor === 'red' && {
                      padding: 0,
                      margin: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: '#4fc3f7',
                      borderRadius: '4px',
                      width: 'fit-content',
                      maxWidth: '100%'
                    })
                  }}
                >
                  <div className="tw-flex tw-gap-2 tw-mb-2">
                    <button
                      onClick={() => handleEditCard(cardData)}
                      className="tw-border-2 tw-border-blue-600 tw-text-blue-600 tw-px-3 tw-py-1 tw-rounded hover:tw-bg-blue-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCard(cardData)}
                      className="tw-border-2 tw-border-red-600 tw-text-red-600 tw-px-3 tw-py-1 tw-rounded hover:tw-bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                  {cardColor === 'default' && (
                    <div 
                      className="card-info" 
                      style={{
                        marginBottom: '10px',
                        fontSize: '14px',
                        color: '#000000',
                        textAlign: 'center'
                      }}
                    >
                      <h3>Branch: {cardData.branch}</h3>
                      <h3>Card ID: {cardData.id}</h3>
                    </div>
                  )}
                  <BingoCard card={cardData.card} cardColor={cardColor} />
                  {cardColor === 'red' && (
                    <div 
                      className="card-info" 
                      style={{
                        marginTop: '-25px',
                        fontSize: '120px',
                        color: '#000000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '100px'
                      }}
                    >
                      <h1 style={{color:'black'}}>{cardData.id}</h1>
                      <h1 style={{color:'black'}}>ቢንጎ</h1>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {hasMoreCards && (
            <div className="tw-flex tw-justify-center tw-mt-6">
              <button 
                onClick={loadMoreCards}
                disabled={loading}
                className="tw-px-6 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More Cards'}
              </button>
            </div>
          )}

          <div className="row justify-start align-center margin-vertical-md">
            <div className="col shrink padding-horizontal-md">
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
              />
              <button onClick={handleGenerateQR}>
                {loading2 ? "generating..." : "generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        className="altBtn"
        onClick={() => {
          window.print();
          return false;
        }}
      >
        Print Cards
      </button>

      {showQRModal && (
        <div className="modal">
          <div className="modal-content" ref={qrRef}>
            <button className="close" onClick={handleCloseQRModal}>
              close
            </button>
            <h2>Scan the QR code to access the PDF</h2>
            <QRCode value={pdfUrl} />
            <button onClick={downloadQRCode}>Download QR Code</button>
          </div>
        </div>
      )}

      {showEditModal && editFormData && (
        <div className="tw-fixed tw-inset-0 tw-flex tw-justify-center tw-items-center tw-bg-gray-800 tw-bg-opacity-75 tw-z-50">
          <div className="tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-max-h-[90vh] tw-overflow-y-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <h2 className="tw-text-2xl tw-font-bold tw-text-blue-800">Edit Cardella {editingCard?.id}</h2>
              <button 
                onClick={handleCloseEditModal}
                className="tw-text-gray-600 hover:tw-text-gray-800 tw-text-2xl tw-font-bold"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateCard}>
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-bold tw-mb-2">ID:</label>
                <input
                  type="number"
                  name="id"
                  value={editFormData.id}
                  onChange={handleEditFormChange}
                  required
                  min="1"
                  className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded"
                />
              </div>
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-bold tw-mb-2">Branch:</label>
                <input
                  type="text"
                  name="branch"
                  value={editFormData.branch}
                  onChange={handleEditFormChange}
                  required
                  readOnly
                  className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded tw-bg-gray-100"
                />
              </div>
              <div className="tw-flex tw-gap-2 tw-mb-4">
                {["B", "I", "N", "G", "O"].map((column) => (
                  <div key={column} className="tw-flex-1">
                    <label className="tw-block tw-text-center tw-text-lg tw-font-bold tw-mb-2 tw-text-blue-600">
                      {column}
                    </label>
                    {editFormData[column].map((val, index) => (
                      <input
                        key={index}
                        type={column === "N" && index === 2 ? "text" : "number"}
                        value={column === "N" && index === 2 ? "Free" : val}
                        onChange={(e) => handleEditArrayChange(e, column, index)}
                        required={column !== "N" || index !== 2}
                        readOnly={column === "N" && index === 2}
                        disabled={loading}
                        min="1"
                        max="99"
                        className="tw-w-full tw-p-2 tw-mb-1 tw-border tw-border-gray-300 tw-rounded"
                        style={{
                          backgroundColor: column === "N" && index === 2 ? "#e0e0e0" : "white",
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="tw-flex tw-gap-3">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="tw-flex-1 tw-px-4 tw-py-2 tw-bg-gray-500 tw-text-white tw-rounded hover:tw-bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="tw-flex-1 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                  {loading ? "Updating..." : "Update Card"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CardFetcher);